/**
 * Whether the record's process waits on a feasibility result (SA-1076).
 *
 * Step 7 of the shipped charter quote is a wait on `feasibility_recorded`:
 * nobody completes it from a task page, a result saved in the record's
 * Feasibility section ends it. The section, the band and the task page read
 * this one rule so the record can say where the step is worked. Pure: the
 * record read in, a fact out.
 */
import type { RecordProcessRead, TaskRead } from '@/types/process'
import type { WorkflowDefinitionJson } from '@/types/workflowDefinition'

export const FEASIBILITY_EVENT = 'feasibility_recorded'
export const FEASIBILITY_SURFACE = 'record.feasibility'

export interface FeasibilityAwait {
  stepNumber: number
  stepTitle: string
  /** When the wait opened: its open wait task's `created_at` (each visit to
   * the step opens a new one). Null when the read carries no such task. */
  since: string | null
}

export function feasibilityAwait(record: RecordProcessRead | null | undefined): FeasibilityAwait | null {
  const step = record?.current_step
  if (!record || record.status !== 'running' || !step || step.waiting_for_event !== FEASIBILITY_EVENT) return null
  const wait = record.open_tasks.find((t) => t.kind === 'wait' && t.step_id === step.id)
  return { stepNumber: step.number, stepTitle: step.title, since: wait?.created_at ?? null }
}

/** Option A: a result shown that was recorded before the wait opened did not
 * end it (a post the process is not waiting for moves nothing, E2 D7). */
export function recordedBeforeWait(recordedAt: string | null | undefined, since: string | null): boolean {
  if (!recordedAt || !since) return false
  const recorded = Date.parse(recordedAt)
  const opened = Date.parse(since)
  return Number.isFinite(recorded) && Number.isFinite(opened) && recorded < opened
}

/** A wait task whose step ends on the record's Feasibility section. */
export function isFeasibilityWait(task: TaskRead | null | undefined): boolean {
  return !!task && task.kind === 'wait' && task.surface === FEASIBILITY_SURFACE
}

/** SA-1079: the step of this definition that waits on `feasibility_recorded`
 * (the shipped template's step 7), else null: an order or graph definition,
 * or a process with no such wait. Keyed on the core event, never a step id. */
export function feasibilityStepId(definition: WorkflowDefinitionJson | null | undefined): string | null {
  if (!definition || definition.kind !== 'process') return null
  for (const [id, step] of Object.entries(definition.steps)) {
    const executor = step.executor
    if (executor.type === 'wait' && 'event' in executor.on && executor.on.event === FEASIBILITY_EVENT) return id
  }
  return null
}

/** SA-1036: whether the process has reached that step, so a feasibility
 * verdict means something: not running, no such step, at it now, or the
 * step in the ledger `path` (a re-open to an earlier step still counts;
 * never reading order, which back-edges break). No record reads as reached:
 * a failed read keeps today's bytes. */
export function feasibilityReached(record: RecordProcessRead | null | undefined, stepId: string | null): boolean {
  if (!record || record.status !== 'running' || stepId === null) return true
  return record.current_step?.id === stepId || record.path.some((entry) => entry.step === stepId)
}
